/**
 * Theme Configuration Interfaces
 *
 * These interfaces define the structure for organization-specific theming.
 * Themes are loaded once at app initialization based on the domain.
 */

export interface ThemeColorPalette {
  main: string;
  light: string;      // -100 variant
  medium: string;     // -400 variant
  dark: string;       // -600 variant
  contrast: string;   // Text color for contrast on this color
}

export interface ThemeColors {
  // Primary brand color (main organizational color)
  primary: ThemeColorPalette;

  // Secondary accent color
  secondary: ThemeColorPalette;

  // Warning/error color
  warn: ThemeColorPalette;

  // Semantic colors
  success: string;
  warning: string;
  error: string;
  info: string;
  purple?: string;

  // Neutral colors
  white: string;
  grey: {
    100: string;
    300: string;
    400: string;
    500: string;
    700?: string;
    900: string;
  };
  black: string;
  lightgrey: string;
  whitesmoke?: string;

  // Background colors
  cardBackground: string;  // Card/panel background color
  pageBackground?: string;  // Page/content background color
  sidebarBackground?: string;  // Sidebar background color (overrides secondary if set)
  sidebarOverlay?: string;  // Sidebar overlay gradient (set to 'none' to disable)
  rimBackground?: string;  // App rim/border background color
  organizationUnitSelectorBackground?: string;  // Organization unit selector card background

  // Text colors
  text: {
    main: string;           // Primary text color
    subdued: string;        // Secondary/muted text
    onPrimary: string;      // Text on primary color backgrounds
    onSecondary: string;    // Text on secondary color backgrounds
    onWarn: string;         // Text on warn color backgrounds
    filterHeading?: string; // Filter heading color (defaults to primary if not set)
    sidebarIcon?: string;   // Sidebar icon color (defaults to white if not set)
    sidebarText?: string;   // Sidebar text color (defaults to white if not set)
  };

  // Transparent overlays
  transparent: {
    0: string;    // Fully transparent
    50: string;
    120: string;
    140: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    900: string;
  };

  // Context-specific colors
  alert: string;
  alertLight?: string;
  attachmentLink: string;

  // Box shadows
  videoBoxShadow?: string;

  // Status colors
  status: {
    1: { bg: string; color?: string };
    2: { bg: string; color?: string };
    3: { bg: string; color?: string };
    4: { bg: string; color: string };
    5: { bg: string; color: string };
  };

  // Organization type colors
  bso: string;
  kdv: string;

  // Message colors
  message: {
    subtitle: string;
    paragraph: string;
  };

}

export interface ThemeFonts {
  // Global fonts
  primary: string;        // Main body font (e.g., 'Roboto, sans-serif')
  secondary?: string;     // Alternative font for headings/special elements
  logo?: string;          // Logo font (e.g., 'Moranga, sans-serif')

  // Area-specific fonts
  sidebar: string;        // Sidebar navigation font
  content: string;        // Main content area font

  // Font sizes
  sizes: {
    xs: string;           // Extra small
    sm: string;           // Small
    base: string;         // Base font size
    md: string;           // Medium
    lg: string;           // Large
    xl: string;           // Extra large
  };

  // Font weights
  weights: {
    light: number;
    normal: number;
    medium: number;
    semibold: number;
    bold: number;
  };
}

export interface ThemeUI {
  // Border radius settings
  borderRadius: {
    none: string;         // 0
    sm: string;           // Small (e.g., '4px')
    md: string;           // Medium (e.g., '8px')
    lg: string;           // Large (e.g., '16px')
    xl: string;           // Extra large (e.g., '24px')
    full: string;         // Fully rounded (e.g., '9999px')
  };

  // Spacing unit (optional, for future use)
  spacing?: {
    unit: string;         // Base spacing unit (e.g., '8px')
  };

  // Rim width (app border/inset width)
  rimWidth?: string;      // Width of the app rim/border (e.g., '24px')

  // Avatar border (set to 'none' or '0' to hide border when using decorative background)
  avatarBorder?: string;  // Avatar border style (e.g., '3px solid white', 'none')

  // Logo styling
  logoMaxHeight?: string;        // Max height for logo (e.g., '60px', '44px')
  logoJustify?: string;          // Logo container justify-content (e.g., 'center', 'flex-start', 'flex-end')

  // Shadow settings
  shadows?: {
    light: string;          // Light shadow (e.g., for cards)
    medium: string;         // Medium shadow
    heavy: string;          // Heavy shadow
  };
}

export interface ThemeAssets {
  // Logo variations
  logo: string;                     // Primary logo URL
  logoComponent?: any;              // Optional custom logo component (Type reference)
  logoTitle?: string;               // Optional custom logo title text (for custom logo components)
  logoCompact?: string;             // Compact logo for collapsed sidebar
  poweredByLogo: string;            // Footer "powered by" logo

  // Background assets
  sidebarBackground?: string;       // Sidebar background pattern image URL
  avatarBackground?: string;        // Avatar decorative background image URL

  // Avatar images
  avatarImg?: string;               // Avatar image URL

  // Placeholder images
  activityPlaceholder: string;      // Default activity image
  activityBlockedImage?: string;    // Blocked activity image (default: './assets/images/activities/activity-blocked.jpg')
  activity404Image?: string;        // 404/not found activity image
  favicon?: string;                 // Optional favicon URL for the theme
}

export interface ThemeIcons {
  activities: string;               // Activities icon (default: 'filter_none')
  baseActivities: string;           // Base activities icon (default: 'filter_none')
  examples: string;                 // Examples/templates icon (default: 'library_books')
  baseExamples: string;             // Base examples icon (default: 'library_books')
  calendar: string;                 // Calendar icon (default: 'calendar_today')
  pinboard: string;                 // Pinboard icon (default: 'push_pin')
  organizationUsers: string;        // Organization users icon (default: 'account_box')
  organizationDetails: string;      // Organization details icon (default: 'domain')
  settings: string;                 // Settings icon (default: 'settings')
  activeUsers: string;              // Active users icon (default: 'admin_panel_settings')
  insights: string;                 // Insights icon (default: 'insights')
  newsItems: string;                // News items icon (default: 'article')
  baseNewsItems: string;            // Base news items icon (default: 'article')
  reviews: string;                  // Reviews icon (default: 'reviews')
}

export interface OrganizationTheme {
  // Identification
  id: string;                       // Unique theme ID (e.g., 'doenkids', 'beinspired')
  name: string;                     // Display name (e.g., 'DoenKids', 'BeInspired')
  brandName?: string;               // marketing name for copy (fallback: name)

  // Domain matching
  domains: string[];                // List of domains this theme applies to

  // Theme configuration
  colors: ThemeColors;
  fonts: ThemeFonts;
  ui: ThemeUI;
  assets: ThemeAssets;
  icons?: ThemeIcons;               // Optional custom icons (falls back to Material Icons if not provided)

  // Auth0 specific configuration
  auth0?: {
    logoUrl: string;
    themeColor: string;
  };
}

/**
 * CSS Custom Property Names
 * Used for injecting theme values into :root
 */
export const CSS_THEME_VARS = {
  // Colors
  PRIMARY: '--theme-primary',
  PRIMARY_LIGHT: '--theme-primary-light',
  PRIMARY_MEDIUM: '--theme-primary-medium',
  PRIMARY_DARK: '--theme-primary-dark',
  PRIMARY_CONTRAST: '--theme-primary-contrast',

  SECONDARY: '--theme-secondary',
  SECONDARY_LIGHT: '--theme-secondary-light',
  SECONDARY_MEDIUM: '--theme-secondary-medium',
  SECONDARY_DARK: '--theme-secondary-dark',
  SECONDARY_CONTRAST: '--theme-secondary-contrast',

  WARN: '--theme-warn',
  WARN_LIGHT: '--theme-warn-light',
  WARN_MEDIUM: '--theme-warn-medium',
  WARN_DARK: '--theme-warn-dark',

  SUCCESS: '--theme-success',
  WARNING: '--theme-warning',
  ERROR: '--theme-error',
  ERROR_LIGHT: '--theme-error-light',
  INFO: '--theme-info',
  PURPLE: '--theme-purple',

  WHITE: '--theme-white',
  BLACK: '--theme-black',
  GREY: '--theme-grey',
  GREY_100: '--theme-grey-100',
  GREY_300: '--theme-grey-300',
  GREY_400: '--theme-grey-400',
  GREY_500: '--theme-grey-500',
  GREY_700: '--theme-grey-700',
  GREY_900: '--theme-grey-900',
  LIGHTGREY: '--theme-lightgrey',
  WHITESMOKE: '--theme-whitesmoke',

  TEXT_LINK: '--theme-text-link',
  TEXT_MAIN: '--theme-text-main',
  TEXT_SUBDUED: '--theme-text-subdued',
  TEXT_ON_PRIMARY: '--theme-text-on-primary',
  TEXT_ON_SECONDARY: '--theme-text-on-secondary',
  TEXT_ON_WARN: '--theme-text-on-warn',
  TEXT_FILTER_HEADING: '--theme-text-filter-heading',
  TEXT_SIDEBAR_ICON: '--theme-text-sidebar-icon',
  TEXT_SIDEBAR_TEXT: '--theme-text-sidebar-text',

  CARD_BACKGROUND: '--theme-card-background',
  PAGE_BACKGROUND: '--theme-page-background',
  SIDEBAR_BACKGROUND: '--theme-sidebar-background',
  SIDEBAR_OVERLAY: '--theme-sidebar-overlay',
  RIM_BACKGROUND: '--theme-rim-background',
  ORG_UNIT_SELECTOR_BACKGROUND: '--theme-org-unit-selector-background',

  // Fonts
  FONT_PRIMARY: '--theme-font-primary',
  FONT_SECONDARY: '--theme-font-secondary',
  FONT_LOGO: '--theme-font-logo',          // Logo font
  FONT_HEADING: '--theme-font-heading',    // Shortcut for headings
  FONT_BODY: '--theme-font-body',          // Shortcut for body text
  FONT_SIDEBAR: '--theme-font-sidebar',
  FONT_CONTENT: '--theme-font-content',

  FONT_SIZE_XS: '--theme-font-size-xs',
  FONT_SIZE_SM: '--theme-font-size-sm',
  FONT_SIZE_BASE: '--theme-font-size-base',
  FONT_SIZE_MD: '--theme-font-size-md',
  FONT_SIZE_LG: '--theme-font-size-lg',
  FONT_SIZE_XL: '--theme-font-size-xl',

  FONT_WEIGHT_LIGHT: '--theme-font-weight-light',
  FONT_WEIGHT_NORMAL: '--theme-font-weight-normal',
  FONT_WEIGHT_MEDIUM: '--theme-font-weight-medium',
  FONT_WEIGHT_SEMIBOLD: '--theme-font-weight-semibold',
  FONT_WEIGHT_BOLD: '--theme-font-weight-bold',

  // UI
  BORDER_RADIUS_NONE: '--theme-border-radius-none',
  BORDER_RADIUS_SM: '--theme-border-radius-sm',
  BORDER_RADIUS_MD: '--theme-border-radius-md',
  BORDER_RADIUS_LG: '--theme-border-radius-lg',
  BORDER_RADIUS_XL: '--theme-border-radius-xl',
  BORDER_RADIUS_FULL: '--theme-border-radius-full',

  SHADOW_LIGHT: '--theme-shadow-light',
  SHADOW_MEDIUM: '--theme-shadow-medium',
  SHADOW_HEAVY: '--theme-shadow-heavy',

  RIM_WIDTH: '--theme-rim-width',
  AVATAR_BORDER: '--theme-avatar-border',
  LOGO_MAX_HEIGHT: '--theme-logo-max-height',
  LOGO_JUSTIFY: '--theme-logo-justify',

  // Transparent overlays
  TRANSPARENT: '--theme-transparent',
  TRANSPARENT_50: '--theme-transparent-50',
  TRANSPARENT_120: '--theme-transparent-120',
  TRANSPARENT_140: '--theme-transparent-140',
  TRANSPARENT_200: '--theme-transparent-200',
  TRANSPARENT_300: '--theme-transparent-300',
  TRANSPARENT_400: '--theme-transparent-400',
  TRANSPARENT_500: '--theme-transparent-500',
  TRANSPARENT_600: '--theme-transparent-600',
  TRANSPARENT_700: '--theme-transparent-700',
  TRANSPARENT_900: '--theme-transparent-900',

  // Status colors
  STATUS_1_BG: '--theme-status-1-bg',
  STATUS_2_BG: '--theme-status-2-bg',
  STATUS_3_BG: '--theme-status-3-bg',
  STATUS_4_BG: '--theme-status-4-bg',
  STATUS_4_COLOR: '--theme-status-4-color',
  STATUS_5_BG: '--theme-status-5-bg',
  STATUS_5_COLOR: '--theme-status-5-color',

  // Organization types
  BSO: '--theme-bso',
  KDV: '--theme-kdv',

  // Message colors
  MESSAGE_SUBTITLE: '--theme-message-subtitle',
  MESSAGE_PARAGRAPH: '--theme-message-paragraph',

  // Box shadows
  SHADOW_VIDEO: '--theme-shadow-video',

  // Assets (for use in CSS)
  SIDEBAR_BG: '--theme-sidebar-bg',
  AVATAR_BG: '--theme-avatar-bg',
  AVATAR_IMG: '--theme-avatar-img',
} as const;

/**
 * Angular Material palette metadata
 *
 * We expose the palette types and supported hue keys so the theme service
 * can derive CSS custom properties for Mat components from the active theme.
 */
export const MATERIAL_COLOR_TYPES = ['primary', 'secondary', 'warn'] as const;
export type MaterialColorType = (typeof MATERIAL_COLOR_TYPES)[number];

export const MATERIAL_COLOR_HUES = [
  '50',
  '100',
  '200',
  '300',
  '400',
  '500',
  '600',
  '700',
  '800',
  '900',
  'A100',
  'A200',
  'A400',
  'A700',
] as const;
export type MaterialColorHue = (typeof MATERIAL_COLOR_HUES)[number];
